import React, { useEffect, useState } from 'react';
import { observer } from 'mobx-react-lite';
import { getTranslationFor } from 'src/locale/lang';
import {
    LoginFormWrapper,
    LoginFormInputLabel,
    LoginFormInput,
    LoginButton,
    LoginFormInputWrapper,
    LoginFormError,
} from 'src/components/LoginForm/LoginForm.style';
import { useAppStateContext } from 'src/AppState/AppState';
import { action, autorun, makeAutoObservable, observable } from 'mobx';
import { ReduxAction } from 'src/AppState/ReduxAction';
import { Common, useCommon } from 'src/AppState/Common';

class LoginState {
    @observable public email: string = '';
    @observable public password: string = '';
    @observable public emptyInputValue: boolean = false;

    @observable public emailError: string | null = null;
    @observable public passwordError: string | null = null;

    public constructor(
        private readonly reduxAction: ReduxAction,
        common: Common
    ) {
        const isDevMode = common.appEnvParams.isProdMode === false;
        if (isDevMode) {
            this.readLoginFromParams();
        }
        makeAutoObservable(this);
    }

    @action private readLoginFromParams = (): void => {
        const params = new URLSearchParams(window.location.search);
        const email = params.get('username')?.replace('[dot]', '.') ?? null;
        const password = params.get('password')?.replace('[dot]', '.') ?? null;
        if (email !== null && password !== null) {
            this.email = email;
            this.password = password;
        }
    };

    @action public changeEmail = (e: React.ChangeEvent<HTMLInputElement>): void => {
        this.email = e.target.value;
        this.emailError = null;
    };

    @action public changePassword = (e: React.ChangeEvent<HTMLInputElement>): void => {
        this.password = e.target.value;
        this.passwordError = null;
    };

    @action public onLogin = (): void => {
        if (this.emailError !== null || this.passwordError !== null) {
            this.reduxAction.onLoginClear();
        }
        this.reduxAction.onLoginRequest(this.email, this.password);
    };

    @action public validateEmail = (): void => {
        if (this.email.length === 0) {
            this.emailError = getTranslationFor('ERROR_REQUIRED_FIELD');
        }
    };

    @action public validatePassword = (): void => {
        if (this.password.length === 0) {
            this.passwordError = getTranslationFor('ERROR_REQUIRED_FIELD');
        }
    };
}

export const LoginForm = observer(() => {
    const { reduxAction, routerBackoffice, reduxLoginState } = useAppStateContext();
    const common = useCommon();
    const [state] = useState(() => new LoginState(reduxAction, common));

    useEffect((): (() => void) => {
        const dispose = autorun(() => {
            if (reduxLoginState.isLoggedIn === true) {
                routerBackoffice.redirectToGlobal();
            }
        });
        return dispose;
    }, []);

    return (
        <LoginFormWrapper>
            <LoginFormInputWrapper>
                <LoginFormInputLabel isInvalidValue={state.emailError !== null}>
                    {getTranslationFor('USER_EMAIL_LABEL')}
                </LoginFormInputLabel>
                <LoginFormInput
                    type='text'
                    name='email'
                    value={state.email}
                    onChange={state.changeEmail}
                    onBlur={state.validateEmail}
                    isInvalid={state.emailError !== null}
                    data-test='email-input'
                />
                {state.emailError === null ? null : (
                    <LoginFormError data-test='login-form-error'>{state.emailError}</LoginFormError>
                )}
            </LoginFormInputWrapper>

            <LoginFormInputWrapper>
                <LoginFormInputLabel isInvalidValue={state.passwordError !== null}>
                    {getTranslationFor('USER_PASSWORD_LABEL')}
                </LoginFormInputLabel>
                <LoginFormInput
                    type='password'
                    value={state.password}
                    onChange={state.changePassword}
                    onBlur={state.validatePassword}
                    isInvalid={state.passwordError !== null}
                    data-test='password-input'
                />
                {state.passwordError === null ? null : (
                    <LoginFormError data-test='login-form-error'>{state.passwordError}</LoginFormError>
                )}
            </LoginFormInputWrapper>

            {reduxLoginState.loginError === null ? null : (
                <LoginFormError data-test='login-form-error'>{reduxLoginState.loginError}</LoginFormError>
            )}

            <LoginButton
                onClick={state.onLogin}
                data-test='log-in-button'
            >
                {getTranslationFor('LOGIN_SUBMIT_BUTTON')}
            </LoginButton>
        </LoginFormWrapper>
    );
});
